import axios from 'axios';
import { ChatThread, ChatMessage, ChatAttachment, CNSOfficer, ThreadFilters } from '../types/chat';
import { API_BASE_URL } from '../config/apiConfig';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const chatService = {
  // Create a new thread with a hotel
  createThread: async (hotelId: string, recipientType: string = 'hotel'): Promise<ChatThread> => {
    const response = await api.post('/api/v1/chat/threads', { hotel_id: hotelId, recipient_type: recipientType });
    return response.data;
  },

  // Get all threads with filters
  getThreads: async (filters?: ThreadFilters): Promise<ChatThread[]> => {
    const response = await api.get('/api/v1/chat/threads', { params: filters });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.threads)) return data.threads;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  },

  // Get thread details
  getThread: async (threadId: string): Promise<ChatThread> => {
    const response = await api.get(`/api/v1/chat/threads/${threadId}`);
    return response.data;
  },

  // Update thread status
  updateThreadStatus: async (threadId: string, status: string): Promise<ChatThread> => {
    const response = await api.put(`/api/v1/chat/threads/${threadId}/status`, { status });
    return response.data;
  },

  // Assign thread to officer
  assignThread: async (threadId: string, officerId: string): Promise<ChatThread> => {
    const response = await api.put(`/api/v1/chat/threads/${threadId}/assign`, { 
      officer_id: officerId 
    });
    return response.data;
  },

  // Mark thread as urgent
  markUrgent: async (threadId: string): Promise<ChatThread> => {
    const response = await api.put(`/api/v1/chat/threads/${threadId}/status`, { 
      priority: 'urgent' 
    });
    return response.data;
  },

  // Send a message
  sendMessage: async (threadId: string, content: string): Promise<ChatMessage> => {
    const response = await api.post(`/api/v1/chat/threads/${threadId}/messages`, { content });
    return response.data.data || response.data;
  },

  // Get messages for a thread
  getMessages: async (threadId: string, page: number = 1, limit: number = 50): Promise<ChatMessage[]> => {
    const response = await api.get(`/api/v1/chat/threads/${threadId}/messages`, {
      params: { page, page_size: limit },
    });
    return response.data.messages || response.data.data || response.data;
  },

  // Upload attachment
  uploadAttachment: async (messageId: string, file: File): Promise<ChatAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`/api/v1/chat/messages/${messageId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data || response.data;
  },

  // Get attachments for a message
  getAttachments: async (messageId: string): Promise<ChatAttachment[]> => {
    const response = await api.get(`/api/v1/chat/messages/${messageId}/attachments`);
    return response.data.attachments || response.data.data || response.data;
  },

  // Download attachment
  downloadAttachment: async (messageId: string, attachmentId: string): Promise<Blob> => {
    const response = await api.get(`/api/v1/chat/messages/${messageId}/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get available CNS officers
  getAvailableOfficers: async (): Promise<CNSOfficer[]> => {
    const response = await api.get('/api/v1/users/officers');
    return response.data;
  },

  // Get chat statistics
  getStats: async (): Promise<any> => {
    const response = await api.get('/api/v1/chat/stats');
    return response.data;
  },

  // Search messages
  searchMessages: async (query: string, filters?: any): Promise<ChatMessage[]> => {
    const response = await api.get('/api/v1/chat/search', {
      params: { q: query, ...filters },
    });
    return response.data;
  },

  // Export conversation
  exportConversation: async (threadId: string): Promise<Blob> => {
    const response = await api.post(`/api/v1/chat/threads/${threadId}/export`, {}, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Internal agency staff chat (CNS ↔ ONT ↔ Admin) — no hotel_id required
  createStaffThread: async (recipientRole: 'ont' | 'cns' | 'anr' | 'admin'): Promise<StaffChatThread> => {
    const response = await api.post('/api/v1/chat/staff/threads', { recipient_role: recipientRole });
    return response.data;
  },

  getStaffThreads: async (): Promise<StaffChatThread[]> => {
    const response = await api.get('/api/v1/chat/staff/threads');
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.threads)) return data.threads;
    return [];
  },

  getStaffMessages: async (threadId: string): Promise<StaffChatMessage[]> => {
    const response = await api.get(`/api/v1/chat/staff/threads/${threadId}/messages`, {
      params: { page: 1, page_size: 75 },
    });
    return response.data.messages || response.data.data || response.data || [];
  },

  sendStaffMessage: async (threadId: string, content: string): Promise<StaffChatMessage> => {
    const response = await api.post(`/api/v1/chat/staff/threads/${threadId}/messages`, { content });
    return response.data.data || response.data;
  },
};

export interface StaffChatThread {
  id: string;
  requester_id: string;
  requester_role: string;
  recipient_role: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface StaffChatMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  sender_role: string;
  content: string;
  delivered_at?: string;
  read_at?: string;
  created_at: string;
}

type MessageHandler = (message: ChatMessage) => void;
type StaffMessageHandler = (message: StaffChatMessage) => void;
type PresenceHandler = () => void;

class ChatWebSocket {
  private ws: WebSocket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private staffMessageHandlers: StaffMessageHandler[] = [];
  private presenceHandlers: PresenceHandler[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  connect() {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;

    // Chat realtime is on the API gateway at /api/v1/chat/realtime.
    // Do NOT prefix with WS_URL (/notifications/ws) — that path is for security alerts only.
    const base = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

    try {
      this.ws = new WebSocket(`${base}/api/v1/chat/realtime?token=${encodeURIComponent(token)}`);
    } catch (error) {
      console.error('Chat WebSocket connect failed:', error);
      this.reconnectTimer = setTimeout(() => this.connect(), 5000);
      return;
    }

    this.ws.onopen = () => {
      this.presenceHandlers.forEach((handler) => handler());
    };

    this.ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if ((payload.type === 'chat.message' || payload.type === 'chat.message.urgent') && payload.data) {
          this.messageHandlers.forEach((handler) => handler(payload.data));
        } else if (payload.type === 'chat.staff.message' && payload.data) {
          const staffMsg: StaffChatMessage = {
            id: payload.data.message_id || payload.data.id,
            thread_id: payload.data.thread_id,
            sender_id: payload.data.sender_id,
            sender_role: payload.data.sender_role,
            content: payload.data.content,
            created_at: payload.data.created_at,
          };
          this.staffMessageHandlers.forEach((handler) => handler(staffMsg));
        } else if (payload.type === 'presence.update') {
          this.presenceHandlers.forEach((handler) => handler());
        }
      } catch (error) {
        console.error('Error parsing chat WebSocket message:', error);
      }
    };

    this.ws.onclose = () => {
      this.ws = null;
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => this.connect(), 5000);
    };
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.ws;
    this.ws = null;
    if (socket) {
      socket.onclose = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    }
  }

  onMessage(handler: MessageHandler) {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter((item) => item !== handler);
    };
  }

  onPresence(handler: PresenceHandler) {
    this.presenceHandlers.push(handler);
    return () => {
      this.presenceHandlers = this.presenceHandlers.filter((item) => item !== handler);
    };
  }

  onStaffMessage(handler: StaffMessageHandler) {
    this.staffMessageHandlers.push(handler);
    return () => {
      this.staffMessageHandlers = this.staffMessageHandlers.filter((item) => item !== handler);
    };
  }
}

export const chatWebSocket = new ChatWebSocket();
